import React, { useState } from "react"
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummaryProps,
  Collapse,
  SxProps,
  styled,
} from "@mui/material"

type Props = {
  sx?: SxProps
  keepHeader?: boolean
  collapsed: React.ReactElement
  expanded: React.ReactNode
}

const IconArrowDown = styled("svg")(({ theme }) => ({
  width: "17px",
  height: "10px",
  fill: "none",
  "& path": {
    stroke: theme.palette.primary.main,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },
}))

const CardAccordionComponent = ({
  keepHeader,
  collapsed,
  expanded,
  sx,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = (event: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded)
  }

  const keepHeaderContainer = keepHeader || !isExpanded

  return (
    <CardAccordion sx={sx} onChange={handleToggle}>
      <Collapsed>
        <CollapseContainer in={keepHeaderContainer}>
          <Container>{collapsed}</Container>
        </CollapseContainer>
      </Collapsed>
      <Expanded>{expanded}</Expanded>
    </CardAccordion>
  )
}

CardAccordionComponent.defaultProps = {
  keepHeader: false,
}

export default CardAccordionComponent

export const CardAccordion = styled(
  ({ children, ...restProps }: AccordionProps) => (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      {...restProps}
    >
      {children}
    </Accordion>
  ),
)(({ theme }) => ({
  border: "none",
  boxShadow: "12px 14px 23px -2px rgb(0 0 0 / 3%)",
  borderRadius: "26px",
  marginBottom: "16px",
  "&.MuiAccordion-root": {
    padding: "22px 32px",
    background: theme.palette.common.white,
    boxShadow: "12px 14px 23px rgba(0, 0, 0, 0.03)",
    borderRadius: "26px",
    [theme.breakpoints.down("lg")]: {
      borderRadius: "21px",
      padding: "16px 21px",
    },
    [theme.breakpoints.down("md")]: {
      borderRadius: "21px",
      padding: "8px 21px",
    },
  },
  "&:not(:last-child)": {
    border: "none",
  },
  "&:before": {
    display: "none",
  },
}))

export const Collapsed = styled(
  ({ children, ...restProps }: AccordionSummaryProps) => (
    <AccordionSummary
      expandIcon={
        <IconArrowDown xmlns="http://www.w3.org/2000/svg">
          <path d="m1 1 7.468 7.503L15.94 1" />
        </IconArrowDown>
      }
      {...restProps}
    >
      {children}
    </AccordionSummary>
  ),
)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  margin: "0",
  padding: 0,
  minHeight: "auto",
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    padding: "18px 0",
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    padding: "7.5px 4px",
  },
}))

const CollapseContainer = styled(Collapse)(() => ({
  flexGrow: 1,
}))

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexGrow: 1,
  margin: "0",
  padding: 0,
  [theme.breakpoints.down("md")]: {
    margin: "0 5px 0 0",
  },
}))

export const Expanded = styled(
  ({ children, ...restProps }: AccordionDetailsProps) => (
    <AccordionDetails {...restProps}>{children}</AccordionDetails>
  ),
)(() => ({
  padding: 0,
  border: "none",
}))
